/**
 * FormConcierge-Api — the browser client for the Form Concierge façade (`/formconcierge/1.0/*`, same origin as the app).
 *
 * The façade holds the plansheet token and enforces tenancy; this client only speaks JSON to it. Every call carries the
 * `X-Form-Concierge: 1` CSRF header (a custom header cannot be sent cross-origin without a preflight the façade never
 * allows), same-origin credentials and `cache: 'no-store'`. Uploads go through XHR (fetch has no upload progress) as a
 * raw-body PUT to `Upload`.
 *
 * `fetch` and `XMLHttpRequest` are injectable for tests; otherwise they are read from `window` at CALL time, never at
 * load time (the module is required by tests and by the bundle before any window exists).
 */

const DEFAULT_BASE_URL = '/formconcierge/1.0/';
const MAX_ID = 9999999999;

/**
 * Does nothing (the abort of an upload that never started).
 */
const noop = () =>
{
	// nothing to do
};

/**
 * An error answered by (or on the way to) the façade.
 * `.Status` is the HTTP status (0 = no response), `.Code` is the body's `Error` (or a client code such as
 * 'NetworkError', 'BadResponse', 'Aborted', 'BadRequest'), `.Detail` the body's `Detail`, `.Body` the parsed body or null.
 */
class FormConciergeApiError extends Error
{
	/**
	 * @param {string} pMessage - Human-readable message.
	 * @param {{ Status?: number, Code?: string, Detail?: string, Body?: any }} [pInfo] - What the façade answered.
	 */
	constructor(pMessage, pInfo)
	{
		super(pMessage);
		const tmpInfo = pInfo || {};
		this.name = 'FormConciergeApiError';
		/** @type {number} */
		this.Status = Number(tmpInfo.Status) || 0;
		/** @type {string} */
		this.Code = (typeof tmpInfo.Code === 'string') ? tmpInfo.Code : '';
		/** @type {string} */
		this.Detail = (typeof tmpInfo.Detail === 'string') ? tmpInfo.Detail : '';
		/** @type {any} */
		this.Body = (tmpInfo.Body === undefined) ? null : tmpInfo.Body;
	}
}

/**
 * Build the error for a non-2xx answer from its status and (maybe) parsed body.
 * @param {number} pStatus - HTTP status.
 * @param {any} pBody - Parsed JSON body, or null when it was not JSON.
 * @return {FormConciergeApiError}
 */
const errorFromResponse = (pStatus, pBody) =>
{
	const tmpBody = (pBody && (typeof pBody === 'object')) ? pBody : null;
	const tmpCode = (tmpBody && (typeof tmpBody.Error === 'string')) ? tmpBody.Error : '';
	const tmpDetail = (tmpBody && (typeof tmpBody.Detail === 'string')) ? tmpBody.Detail : '';
	let tmpMessage = tmpCode || `HTTP ${pStatus}`;
	if (tmpDetail)
	{
		tmpMessage += `: ${tmpDetail}`;
	}
	return new FormConciergeApiError(tmpMessage, { Status: pStatus, Code: tmpCode || `HTTP${pStatus}`, Detail: tmpDetail, Body: tmpBody });
};

/**
 * Parse a response text as JSON; '' → {} (a 2xx with no body), anything unparseable → undefined.
 * @param {string} pText - The raw response text.
 * @return {any}
 */
const parseJSONText = (pText) =>
{
	if ((typeof pText !== 'string') || (pText.trim().length < 1))
	{
		return {};
	}
	try
	{
		return JSON.parse(pText);
	}
	catch (pError)
	{
		return undefined;
	}
};

/**
 * A positive integer id for a path segment, or throw (keeps anything but digits out of the URL).
 * @param {any} pValue - The id as given.
 * @param {string} pName - Its name, for the error.
 * @return {number}
 */
const requireID = (pValue, pName) =>
{
	const tmpID = Number(pValue);
	if (!isFinite(tmpID) || (Math.floor(tmpID) !== tmpID) || (tmpID < 1) || (tmpID > MAX_ID))
	{
		throw new FormConciergeApiError(`BadRequest: ${pName} must be a positive integer`, { Status: 0, Code: 'BadRequest', Detail: pName });
	}
	return tmpID;
};

/**
 * Same-origin client for the Form Concierge façade (E2). Every method returns a promise of the façade's JSON or
 * rejects with a FormConciergeApiError; uploadFile returns `{ promise, abort }`.
 */
class FormConciergeApi
{
	/**
	 * @param {{ BaseURL?: string, fetch?: Function, XMLHttpRequest?: any }} [pOptions] - BaseURL defaults to
	 *   '/formconcierge/1.0/'; fetch / XMLHttpRequest default to window's at call time.
	 */
	constructor(pOptions)
	{
		const tmpOptions = pOptions || {};
		let tmpBaseURL = (typeof tmpOptions.BaseURL === 'string' && tmpOptions.BaseURL.length > 0) ? tmpOptions.BaseURL : DEFAULT_BASE_URL;
		if (tmpBaseURL.charAt(tmpBaseURL.length - 1) !== '/')
		{
			tmpBaseURL += '/';
		}
		/** @type {string} */
		this.BaseURL = tmpBaseURL;
		/** @type {Function|null} */
		this._fetch = (typeof tmpOptions.fetch === 'function') ? tmpOptions.fetch : null;
		/** @type {any} */
		this._XMLHttpRequest = (typeof tmpOptions.XMLHttpRequest === 'function') ? tmpOptions.XMLHttpRequest : null;
	}

	/**
	 * The fetch to use: the injected one, else window.fetch (resolved now, not at load).
	 * @return {Function|null}
	 */
	_resolveFetch()
	{
		if (this._fetch)
		{
			return this._fetch;
		}
		const tmpWindow = (typeof window !== 'undefined') ? /** @type {any} */ (window) : null;
		if (tmpWindow && (typeof tmpWindow.fetch === 'function'))
		{
			return tmpWindow.fetch.bind(tmpWindow);
		}
		return null;
	}

	/**
	 * The XMLHttpRequest constructor to use: the injected one, else window's.
	 * @return {any}
	 */
	_resolveXHR()
	{
		if (this._XMLHttpRequest)
		{
			return this._XMLHttpRequest;
		}
		const tmpWindow = (typeof window !== 'undefined') ? /** @type {any} */ (window) : null;
		if (tmpWindow && (typeof tmpWindow.XMLHttpRequest === 'function'))
		{
			return tmpWindow.XMLHttpRequest;
		}
		return null;
	}

	/**
	 * Send one request and answer `{ Status, Body, Empty }`: Body is the parsed JSON ({} for an empty body, undefined
	 * when the response was not JSON). Rejects only on a network-level failure (FormConciergeApiError 'NetworkError').
	 * @param {string} pMethod - GET / POST / PUT.
	 * @param {string} pPath - Path under BaseURL (may carry a query string).
	 * @param {any} [pBody] - Object to send as JSON; undefined sends no body.
	 * @return {Promise<{ Status: number, Body: any, Empty: boolean }>}
	 */
	async _send(pMethod, pPath, pBody)
	{
		const tmpFetch = this._resolveFetch();
		if (!tmpFetch)
		{
			throw new FormConciergeApiError('NetworkError: fetch is not available', { Status: 0, Code: 'NetworkError', Detail: 'fetch is not available' });
		}
		/** @type {Record<string, string>} */
		const tmpHeaders = { 'X-Form-Concierge': '1', 'Accept': 'application/json' };
		/** @type {Record<string, any>} */
		const tmpInit = { method: pMethod, credentials: 'same-origin', cache: 'no-store', headers: tmpHeaders };
		if (pBody !== undefined)
		{
			tmpHeaders['Content-Type'] = 'application/json';
			tmpInit.body = JSON.stringify(pBody);
		}
		let tmpResponse;
		let tmpText;
		try
		{
			tmpResponse = await tmpFetch(this.BaseURL + pPath, tmpInit);
			tmpText = await tmpResponse.text();
		}
		catch (pError)
		{
			const tmpDetail = (pError && pError.message) ? String(pError.message) : String(pError);
			throw new FormConciergeApiError(`NetworkError: ${tmpDetail}`, { Status: 0, Code: 'NetworkError', Detail: tmpDetail });
		}
		const tmpEmpty = (typeof tmpText !== 'string') || (tmpText.trim().length < 1);
		return { Status: Number(tmpResponse && tmpResponse.status) || 0, Body: parseJSONText(tmpText), Empty: tmpEmpty };
	}

	/**
	 * A JSON call: 2xx with a JSON object/array → the body; anything else → FormConciergeApiError.
	 * @param {string} pMethod - GET / POST / PUT.
	 * @param {string} pPath - Path under BaseURL.
	 * @param {any} [pBody] - JSON body (POST/PUT).
	 * @return {Promise<any>}
	 */
	async _json(pMethod, pPath, pBody)
	{
		const tmpResult = await this._send(pMethod, pPath, pBody);
		const tmpOK = (tmpResult.Status >= 200) && (tmpResult.Status < 300);
		if (!tmpOK)
		{
			throw errorFromResponse(tmpResult.Status, (tmpResult.Body === undefined) ? null : tmpResult.Body);
		}
		if ((tmpResult.Body === undefined) || (tmpResult.Body === null) || (typeof tmpResult.Body !== 'object'))
		{
			throw new FormConciergeApiError('BadResponse: the Form Concierge service did not answer JSON', { Status: tmpResult.Status, Code: 'BadResponse', Detail: 'not JSON' });
		}
		return tmpResult.Body;
	}

	/**
	 * GET Status. A missing façade (404), a non-JSON answer (e.g. the static server's HTML) or a network error
	 * degrades to `{ Configured:false, Allowed:false, Unreachable:true }` instead of throwing; other JSON errors throw.
	 * @return {Promise<any>}
	 */
	async getStatus()
	{
		const tmpUnreachable = { Configured: false, Allowed: false, Unreachable: true };
		let tmpResult;
		try
		{
			tmpResult = await this._send('GET', 'Status');
		}
		catch (pError)
		{
			return tmpUnreachable;
		}
		if (tmpResult.Status === 404)
		{
			return tmpUnreachable;
		}
		const tmpBody = tmpResult.Body;
		if (tmpResult.Empty || (tmpBody === undefined) || (tmpBody === null) || (typeof tmpBody !== 'object') || Array.isArray(tmpBody))
		{
			return tmpUnreachable;
		}
		if ((tmpResult.Status < 200) || (tmpResult.Status >= 300))
		{
			throw errorFromResponse(tmpResult.Status, tmpBody);
		}
		return tmpBody;
	}

	/**
	 * POST Workspace — find-or-create the customer's library and index anchors.
	 * @return {Promise<{ Workspace: any }>}
	 */
	ensureWorkspace()
	{
		return this._json('POST', 'Workspace', {});
	}

	/**
	 * GET Forms — the customer's indexed forms plus adoptable plansheet form projects.
	 * @return {Promise<{ Forms: Array<any>, Unindexed: Array<any> }>}
	 */
	getForms()
	{
		return this._json('GET', 'Forms');
	}

	/**
	 * POST Form — create a form project and index it.
	 * @param {{ Name: string, Summary?: string }} pBody - The new form.
	 * @return {Promise<{ Form: any }>}
	 */
	createForm(pBody)
	{
		return this._json('POST', 'Form', pBody || {});
	}

	/**
	 * POST Form/:IDProject/Adopt — index an existing plansheet form project for this customer.
	 * @param {number} pIDProject - The plansheet project.
	 * @return {Promise<{ Form: any }>}
	 */
	async adoptForm(pIDProject)
	{
		const tmpID = requireID(pIDProject, 'IDProject');
		return this._json('POST', `Form/${tmpID}/Adopt`, {});
	}

	/**
	 * Upload one file's original bytes to the customer's library (XHR PUT with byte progress).
	 * @param {any} pFile - A File/Blob (`name`, `type`, `size`).
	 * @param {{ onProgress?: (pFraction: number) => void }} [pOptions] - onProgress gets 0..1.
	 * @return {{ promise: Promise<{ IDMedia: number, IDProjectSource: number, Name: string, MimeType: string, FileSizeBytes: number }>, abort: () => void }}
	 */
	uploadFile(pFile, pOptions)
	{
		const tmpOptions = pOptions || {};
		const XHRClass = this._resolveXHR();
		if (!pFile)
		{
			return { promise: Promise.reject(new FormConciergeApiError('BadRequest: no file', { Status: 0, Code: 'BadRequest', Detail: 'file' })), abort: noop };
		}
		if (!XHRClass)
		{
			return { promise: Promise.reject(new FormConciergeApiError('NetworkError: XMLHttpRequest is not available', { Status: 0, Code: 'NetworkError', Detail: 'XMLHttpRequest is not available' })), abort: noop };
		}
		const tmpName = (typeof pFile.name === 'string' && pFile.name.length > 0) ? pFile.name : 'upload';
		const tmpType = (typeof pFile.type === 'string' && pFile.type.length > 0) ? pFile.type : 'application/octet-stream';
		const tmpSize = Number(pFile.size) || 0;
		const tmpURL = `${this.BaseURL}Upload?Name=${encodeURIComponent(tmpName)}&MimeType=${encodeURIComponent(tmpType)}`;
		const tmpXHR = new XHRClass();
		let tmpDone = false;
		let tmpLastFraction = -1;

		/** @param {number} pFraction */
		const fReport = (pFraction) =>
		{
			if (typeof tmpOptions.onProgress !== 'function')
			{
				return;
			}
			const tmpFraction = Math.max(0, Math.min(1, Number(pFraction) || 0));
			if (tmpFraction === tmpLastFraction)
			{
				return;
			}
			tmpLastFraction = tmpFraction;
			try
			{
				tmpOptions.onProgress(tmpFraction);
			}
			catch (pError)
			{
				// a progress painter must never break the upload
			}
		};

		/** @type {(pError: Error|null, pResult?: any) => void} */
		let fFinish = noop;
		const tmpPromise = new Promise((fResolve, fReject) =>
		{
			fFinish = (pError, pResult) =>
			{
				if (tmpDone)
				{
					return;
				}
				tmpDone = true;
				if (pError)
				{
					return fReject(pError);
				}
				return fResolve(pResult);
			};

			tmpXHR.open('PUT', tmpURL, true);
			tmpXHR.setRequestHeader('X-Form-Concierge', '1');
			tmpXHR.setRequestHeader('Accept', 'application/json');
			tmpXHR.setRequestHeader('Content-Type', tmpType);
			if (tmpXHR.upload)
			{
				tmpXHR.upload.onprogress = (pEvent) =>
				{
					if (!pEvent)
					{
						return;
					}
					const tmpTotal = (pEvent.lengthComputable && Number(pEvent.total) > 0) ? Number(pEvent.total) : tmpSize;
					if (tmpTotal > 0)
					{
						fReport(Number(pEvent.loaded) / tmpTotal);
					}
				};
			}
			tmpXHR.onload = () =>
			{
				const tmpStatus = Number(tmpXHR.status) || 0;
				const tmpBody = parseJSONText(tmpXHR.responseText);
				if ((tmpStatus >= 200) && (tmpStatus < 300))
				{
					if ((tmpBody === undefined) || (tmpBody === null) || (typeof tmpBody !== 'object'))
					{
						return fFinish(new FormConciergeApiError('BadResponse: the upload did not answer JSON', { Status: tmpStatus, Code: 'BadResponse', Detail: 'not JSON' }));
					}
					fReport(1);
					return fFinish(null, tmpBody);
				}
				if (tmpStatus === 0)
				{
					return fFinish(new FormConciergeApiError('NetworkError: the upload did not complete', { Status: 0, Code: 'NetworkError', Detail: 'no response' }));
				}
				return fFinish(errorFromResponse(tmpStatus, (tmpBody === undefined) ? null : tmpBody));
			};
			tmpXHR.onerror = () =>
			{
				fFinish(new FormConciergeApiError('NetworkError: the upload failed', { Status: 0, Code: 'NetworkError', Detail: 'upload failed' }));
			};
			tmpXHR.ontimeout = () =>
			{
				fFinish(new FormConciergeApiError('Timeout: the upload timed out', { Status: 0, Code: 'Timeout', Detail: 'upload timed out' }));
			};
			tmpXHR.onabort = () =>
			{
				fFinish(new FormConciergeApiError('Aborted: the upload was cancelled', { Status: 0, Code: 'Aborted', Detail: 'cancelled' }));
			};
			try
			{
				tmpXHR.send(pFile);
			}
			catch (pError)
			{
				const tmpDetail = (pError && pError.message) ? String(pError.message) : String(pError);
				fFinish(new FormConciergeApiError(`NetworkError: ${tmpDetail}`, { Status: 0, Code: 'NetworkError', Detail: tmpDetail }));
			}
		});

		return {
			promise: tmpPromise,
			abort: () =>
			{
				if (tmpDone)
				{
					return;
				}
				try
				{
					tmpXHR.abort();
				}
				catch (pError)
				{
					// already finished
				}
				// Not every XHR fires onabort synchronously (or at all); settle here so the caller never hangs.
				fFinish(new FormConciergeApiError('Aborted: the upload was cancelled', { Status: 0, Code: 'Aborted', Detail: 'cancelled' }));
			},
		};
	}

	/**
	 * POST Form/:IDProject/Sources — link library media and inline text into a form's corpus.
	 * @param {number} pIDProject - The form project.
	 * @param {Array<any>} pSources - `{Kind:'media', IDMedia, Name, Role?, Description?}` | `{Kind:'text', Name, Title?, Body, Role?}`.
	 * @return {Promise<{ Sources: Array<any> }>}
	 */
	async addSources(pIDProject, pSources)
	{
		const tmpID = requireID(pIDProject, 'IDProject');
		return this._json('POST', `Form/${tmpID}/Sources`, { Sources: Array.isArray(pSources) ? pSources : [] });
	}

	/**
	 * GET Form/:IDProject/Sources — the form's corpus (names/roles only, never bodies).
	 * @param {number} pIDProject - The form project.
	 * @return {Promise<{ Sources: Array<any> }>}
	 */
	async getSources(pIDProject)
	{
		const tmpID = requireID(pIDProject, 'IDProject');
		return this._json('GET', `Form/${tmpID}/Sources`);
	}

	/**
	 * POST Session — start a create / update / build run, or an examples follow-up.
	 * @param {{ Flow: string, IDProject: number, Text?: string, DeliverTo?: string, ValidateOnly?: boolean, Force?: boolean, NewSources?: Array<string> }} pBody
	 * @return {Promise<{ IDRequest: number, IDRun?: number, Status?: string, Mode?: string, Warnings?: Array<string> }>}
	 */
	startSession(pBody)
	{
		return this._json('POST', 'Session', pBody || {});
	}

	/**
	 * GET Sessions — recent sessions across the customer's forms, or one form's.
	 * @param {number} [pIDProject] - Limit to one form project.
	 * @return {Promise<{ Sessions: Array<any> }>}
	 */
	async getSessions(pIDProject)
	{
		if (!pIDProject)
		{
			return this._json('GET', 'Sessions');
		}
		const tmpID = requireID(pIDProject, 'IDProject');
		return this._json('GET', `Sessions?IDProject=${tmpID}`);
	}

	/**
	 * GET Session/:IDRequest — the session, its turns, the active run and the form.
	 * @param {number} pIDRequest - The plansheet request.
	 * @return {Promise<any>}
	 */
	async getSession(pIDRequest)
	{
		const tmpID = requireID(pIDRequest, 'IDRequest');
		return this._json('GET', `Session/${tmpID}`);
	}

	/**
	 * POST Session/:IDRequest/Message — send a follow-up turn.
	 * @param {number} pIDRequest - The plansheet request.
	 * @param {{ Text: string, NewSources?: Array<string> }} pBody - The turn.
	 * @return {Promise<{ IDRequest: number }>}
	 */
	async sendMessage(pIDRequest, pBody)
	{
		const tmpID = requireID(pIDRequest, 'IDRequest');
		return this._json('POST', `Session/${tmpID}/Message`, pBody || {});
	}

	/**
	 * POST Session/:IDRequest/Cancel — best-effort cancel of the active run.
	 * @param {number} pIDRequest - The plansheet request.
	 * @return {Promise<{ CancelRequested: boolean, Claimed: boolean }>}
	 */
	async cancelSession(pIDRequest)
	{
		const tmpID = requireID(pIDRequest, 'IDRequest');
		return this._json('POST', `Session/${tmpID}/Cancel`, {});
	}
}

module.exports = { FormConciergeApi, FormConciergeApiError };
